import { Box, Paper, TextField, Tooltip, Typography } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { InfoOutlined } from '@mui/icons-material';
import type { AtterbergLLMethod, AtterbergDetail } from '../../../types/atterberg';
import { computeTrial } from './computations';
import { useTestFormulas } from '../../../hooks/useTestFormulas';

interface TrialsGridProps {
  title: string;
  trials: AtterbergDetail[];
  onChange: (trials: AtterbergDetail[]) => void;
  includeBlows: boolean;
  /** Used to compute single-point LL-n display when LL grid + single-point method. */
  method: AtterbergLLMethod;
  showSinglePointLL: boolean;
  /** FEAT-2026-0004: render an "Avg. Water Content" row spanning trial columns.
   *  On the PL grid this average IS the Plastic Limit (rounded). Mirrors the BGC
   *  PDF template which prints the same row on the PL block. */
  showAvgWaterContent?: boolean;
  readOnly?: boolean;
}

const cellInputSx = {
  '& .MuiInputBase-input': {
    py: 0.4, px: 0.75, fontSize: '0.78rem', textAlign: 'right' as const,
    '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
      WebkitAppearance: 'none', margin: 0,
    },
    MozAppearance: 'textfield',
  },
};

const labelHeader = (label: string, tooltip?: string) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
    <Typography sx={{ fontSize: '0.76rem', fontWeight: 500 }}>{label}</Typography>
    {tooltip && (
      <Tooltip title={tooltip} placement="right">
        <InfoOutlined sx={{ fontSize: 12, color: 'text.disabled' }} />
      </Tooltip>
    )}
  </Box>
);

const fmt = (v: number | null, decimals = 2): string => {
  if (v === null || v === undefined || Number.isNaN(v)) return '—';
  return v.toFixed(decimals);
};

export const TrialsGrid = ({
  title,
  trials,
  onChange,
  includeBlows,
  method,
  showSinglePointLL,
  showAvgWaterContent = false,
  readOnly = false,
}: TrialsGridProps) => {
  const theme = useTheme();
  // Formula strings for computed rows come from the backend Formula Registry
  // (golden-linked to atterbergCalculations.ts) — never hardcoded here.
  const { formulaFor } = useTestFormulas('atterberg');

  const updateField = (
    trialNumber: number,
    field: keyof AtterbergDetail,
    raw: string,
  ) => {
    const next = trials.map((t) => {
      if (t.trialNumber !== trialNumber) return t;
      const value = field === 'containerNo'
        ? (raw || null)
        : (raw === '' ? null : Number(raw));
      return { ...t, [field]: value } as AtterbergDetail;
    });
    onChange(next);
  };

  // Live per-trial computed values
  const computedFor = (t: AtterbergDetail) => computeTrial(t, { method });

  // FEAT-2026-0004: average of trial water contents (= PL on the PL grid).
  // Mirrors the same calculation done by computePL in computations.ts but
  // un-rounded so the live UI shows lab-style 1-decimal precision.
  const avgWaterContent = (() => {
    if (!showAvgWaterContent) return null;
    const wcs: number[] = [];
    for (const t of trials) {
      const c = computedFor(t);
      if (c.waterContent !== null) wcs.push(c.waterContent);
    }
    if (wcs.length === 0) return null;
    return wcs.reduce((a, b) => a + b, 0) / wcs.length;
  })();

  const trialColW = 90;
  const labelColW = 200;

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2, borderRadius: 2,
        border: `1px solid ${alpha(theme.border?.color ?? '#ccc', 0.35)}`,
        backgroundColor: alpha(theme.palette.background.paper, 0.8),
      }}
    >
      <Typography variant="overline" sx={{
        fontWeight: 700, color: 'primary.main', fontSize: '0.7rem', letterSpacing: 0.6,
      }}>
        {title}
      </Typography>

      <Box sx={{ mt: 1, display: 'grid', gridTemplateColumns: `${labelColW}px repeat(${trials.length}, ${trialColW}px) 36px`, rowGap: 0.5, columnGap: 1, alignItems: 'center' }}>
        {/* Header row */}
        <Box />
        {trials.map((t) => (
          <Box key={`hdr-${t.trialNumber}`} sx={{ textAlign: 'center' }}>
            <Typography sx={{ fontSize: '0.78rem', fontWeight: 700 }}>
              Trial {t.trialNumber}
            </Typography>
          </Box>
        ))}
        <Box />

        {/* Container No */}
        {labelHeader('Container No.')}
        {trials.map((t) => (
          <TextField
            key={`ctn-${t.trialNumber}`}
            value={t.containerNo ?? ''}
            size="small"
            onChange={(e) => updateField(t.trialNumber, 'containerNo', e.target.value)}
            sx={cellInputSx}
            disabled={readOnly}
          />
        ))}
        <Box />

        {/* Mass of container */}
        {labelHeader('Mass of container', 'Empty container mass (g)')}
        {trials.map((t) => (
          <TextField
            key={`mc-${t.trialNumber}`}
            value={t.massOfContainer ?? ''}
            type="number" size="small"
            onChange={(e) => updateField(t.trialNumber, 'massOfContainer', e.target.value)}
            sx={cellInputSx}
            disabled={readOnly}
          />
        ))}
        <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>g</Typography>

        {/* Mass wet + container */}
        {labelHeader('Mass of wet soil + ctn.')}
        {trials.map((t) => (
          <TextField
            key={`mw-${t.trialNumber}`}
            value={t.massOfWetSoilAndContainer ?? ''}
            type="number" size="small"
            onChange={(e) => updateField(t.trialNumber, 'massOfWetSoilAndContainer', e.target.value)}
            sx={cellInputSx}
            disabled={readOnly}
          />
        ))}
        <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>g</Typography>

        {/* Mass dry + container */}
        {labelHeader('Mass of dry soil + ctn.')}
        {trials.map((t) => (
          <TextField
            key={`md-${t.trialNumber}`}
            value={t.massOfDrySoilAndContainer ?? ''}
            type="number" size="small"
            onChange={(e) => updateField(t.trialNumber, 'massOfDrySoilAndContainer', e.target.value)}
            sx={cellInputSx}
            disabled={readOnly}
          />
        ))}
        <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>g</Typography>

        {/* No. of blows — LL only */}
        {includeBlows && (
          <>
            {labelHeader('No. of blows (N)')}
            {trials.map((t) => (
              <TextField
                key={`n-${t.trialNumber}`}
                value={t.numberOfBlows ?? ''}
                type="number" size="small"
                onChange={(e) => updateField(t.trialNumber, 'numberOfBlows', e.target.value)}
                sx={cellInputSx}
                disabled={readOnly}
              />
            ))}
            <Box />
          </>
        )}

        {/* Separator */}
        <Box sx={{ gridColumn: '1 / -1', borderTop: `1px dashed ${alpha(theme.palette.divider, 0.7)}`, my: 0.5 }} />

        {/* Computed: water content */}
        {labelHeader('Water content', formulaFor('waterContent')?.formula)}
        {trials.map((t) => {
          const c = computedFor(t);
          return (
            <Box key={`wc-${t.trialNumber}`} sx={{ textAlign: 'right', pr: 1 }}>
              <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: c.waterContent !== null ? 'text.primary' : 'text.disabled' }}>
                {fmt(c.waterContent, 1)}
              </Typography>
            </Box>
          );
        })}
        <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>%</Typography>

        {/* Computed: mass of moist soil */}
        {labelHeader('Mass of moist soil', formulaFor('massOfMoistSoil')?.formula)}
        {trials.map((t) => {
          const c = computedFor(t);
          return (
            <Box key={`mms-${t.trialNumber}`} sx={{ textAlign: 'right', pr: 1 }}>
              <Typography sx={{ fontSize: '0.78rem', color: c.massOfMoistSoil !== null ? 'text.primary' : 'text.disabled' }}>
                {fmt(c.massOfMoistSoil, 2)}
              </Typography>
            </Box>
          );
        })}
        <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>g</Typography>

        {/* Computed: mass of dry soil */}
        {labelHeader('Mass of dry soil', formulaFor('massOfDrySoil')?.formula)}
        {trials.map((t) => {
          const c = computedFor(t);
          return (
            <Box key={`mds-${t.trialNumber}`} sx={{ textAlign: 'right', pr: 1 }}>
              <Typography sx={{ fontSize: '0.78rem', color: c.massOfDrySoil !== null ? 'text.primary' : 'text.disabled' }}>
                {fmt(c.massOfDrySoil, 2)}
              </Typography>
            </Box>
          );
        })}
        <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>g</Typography>

        {/* FEAT-2026-0004: Avg. Water Content — shown on the PL grid as a single
            value spanning all trial columns. Equals the Plastic Limit (before
            rounding). Matches the BGC PDF template's "Avg. Water Content (%)"
            colspan row. */}
        {showAvgWaterContent && (
          <>
            {labelHeader('Avg. Water Content', 'Average of trial water contents — equals the Plastic Limit (before rounding to integer).')}
            <Box
              sx={{
                gridColumn: `2 / span ${trials.length}`,
                textAlign: 'right',
                pr: 1,
              }}
            >
              <Typography sx={{
                fontSize: '0.85rem',
                fontWeight: 700,
                color: avgWaterContent !== null ? 'primary.main' : 'text.disabled',
              }}>
                {fmt(avgWaterContent, 1)}
              </Typography>
            </Box>
            <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>%</Typography>
          </>
        )}

        {/* Single-point LL — only when applicable */}
        {showSinglePointLL && (
          <>
            {labelHeader('LL-n (single-point)', formulaFor('singlePointLL')?.formula)}
            {trials.map((t) => {
              const c = computedFor(t);
              return (
                <Box key={`sp-${t.trialNumber}`} sx={{ textAlign: 'right', pr: 1 }}>
                  <Typography sx={{ fontSize: '0.78rem', color: c.singlePointLL !== null ? 'text.primary' : 'text.disabled' }}>
                    {fmt(c.singlePointLL, 2)}
                  </Typography>
                </Box>
              );
            })}
            <Box />
          </>
        )}
      </Box>
    </Paper>
  );
};
